import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { BankAccount } from 'src/features/billingEntityBankAccounts/utils/types';
import BillingEntityBankAccountFormModal from 'src/features/billingEntityBankAccounts/components/forms/BillingEntityBankAccountFormModal';
import { useUpdateBillingEntityBankAccount } from 'src/features/billingEntityBankAccounts/api/useUpdateBillingEntityBankAccount';
import { LIST_BILLING_ENTITY_BANK_ACCOUNTS_RESOURCE } from 'src/features/billingEntityBankAccounts/api/useListBillingEntityBankAccounts';
import { bankAccountMessages } from 'src/features/billingEntityBankAccounts/utils/messages';

interface UpdateBillingEntityBankAccountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBankAccount: BankAccount;
    children?: React.ReactNode;
}

function UpdateBillingEntityBankAccountFormModal<TData>({
    className,
    semantics,
    children,
    selectedBankAccount,
}: UpdateBillingEntityBankAccountFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateBillingEntityBankAccount({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_ENTITY_BANK_ACCOUNTS_RESOURCE] });
            toast({
                title: formatMessage(bankAccountMessages.updateBankAccountSuccess),
                description: formatMessage(bankAccountMessages.updateBankAccountSuccessDescription, {
                    name: name,
                }),
            });
        },
    });

    return (
        <BillingEntityBankAccountFormModal
            selectedBankAccount={selectedBankAccount}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, bankAccountId: selectedBankAccount.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(bankAccountMessages.editBankAccountSubtitle, {
                name: selectedBankAccount.name,
            })}
        >
            {children}
        </BillingEntityBankAccountFormModal>
    );
}

export default UpdateBillingEntityBankAccountFormModal;
